<template>
    <b-modal 
      id="TRTStatsModal" 
      title="Statistic Report"
      content-class="modal-content-full"    
      dialog-class="modal-dialog-full"
      modal-header-class="ptv-modal-header"
      size="sm"
      centered        
      @hidden="reset()"
      @show="generateStatistics()"
    >
        <!-- Modal Title -->
        <template #modal-header>
            <div class="d-flex align-items-center justify-content-between w-100">
                <div class="modal-title-wrap">
                    <fa :icon="['fas', 'chart-bar']" class="modal-title-icon" />
                    <h5 class="mb-0 font-weight-semibold text-white">Statistic Report</h5>
                </div>
                <span
                    v-if="!dataLoading"
                    class="download-button-container"
                    title="Download report"
                    @click="downloadReports"
                >
                    <fa :icon="['fas', 'download']" size="lg"/>
                </span>
            </div>
        </template>

       <div class="trt-form-area">
            <div class="trt-form-row">
                <div class="trt-field trt-field--type">
                    <label class="trt-form-label">Delivery Type</label>
                    <b-form-select v-model="deliveryType">
                        <template #first>
                            <b-form-select-option :value="null" disabled>Select Type</b-form-select-option>
                        </template>
                        <b-form-select-option value="INPX">INPX</b-form-select-option>
                    </b-form-select>
                </div>
                <div class="trt-field trt-field--date">
                    <label class="trt-form-label text-nowrap">Requested Delivery Date</label>
                    <b-form-input
                        type="date"
                        placeholder="Select date"
                        format="yyyy/mm/dd"
                        v-model="deliveryDate"
                    ></b-form-input>
                </div>
                <div class="trt-field trt-field--notes">
                    <label class="trt-form-label">Notes</label>
                    <b-form-textarea v-model="note" rows="1" max-rows="3"></b-form-textarea>
                </div>
            </div>
        </div>

        <div class="stats-data px-3 pt-3" v-if="statsObjs.length">
            <!-- Section title + single-sample request -->
            <div class="stats-header-row">
                <span class="trt-section-title">Statistical Analysis</span>
                <div class="d-flex align-items-center">
                    <transition name="fade">
                        <span v-if="isCellsLoading" class="stats-loading-banner d-flex align-items-center mr-3">
                            <span class="spinner-border spinner-border-sm loading-banner-spinner mr-2" role="status"></span>
                            <span class="loading-banner-text">Statistics are loading…</span>
                        </span>
                    </transition>
                    <b-button
                        v-if="showRequestBtn && statsObjs.length === 1"
                        :disabled="dataLoading"
                        variant="none"
                        class="request-btn"
                        @click="openConfirmation(statsObjs[0], 0)"
                    >
                        <fa :icon="['fas', 'paper-plane']" class="mr-1" /> Request
                    </b-button>
                </div>
            </div>

            <!-- One card group per sample/polygon -->
            <div class="sample-block" v-for="(statsObj, index) in statsObjs" :key="index">
                <div class="sample-block-header" v-if="statsObjs.length > 1">
                    <span class="sample-block-title">Sample {{ index + 1 }}</span>
                    <b-button
                        v-if="showRequestBtn"
                        :disabled="dataLoading"
                        variant="none"
                        class="request-btn"
                        @click="openConfirmation(statsObj, index)"
                    >
                        <fa :icon="['fas', 'paper-plane']" class="mr-1" /> Request {{ index + 1 }}
                    </b-button>
                </div>

                <!-- Summary card (full width) -->
                <div class="stat-card stat-card-summary">
                    <div class="stat-card-header stat-card-header-dark">
                        <fa :icon="['fas', summarySection.icon]" class="mr-2" />
                        <span>{{ summarySection.title }}</span>
                    </div>
                    <div class="stat-card-summary-body">
                        <div class="summary-metric" v-for="row in summarySection.rows" :key="row.key">
                            <span class="summary-metric-label">{{ row.label }}</span>
                            <span class="summary-metric-value">
                                <template v-if="statsObj[row.key] !== undefined">{{ statsObj[row.key] }}</template>
                                <span v-else-if="dataLoading" class="spinner-border spinner-border-sm cell-spinner" role="status"></span>
                                <template v-else>{{ row.fallback }}</template>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Detail cards, masonry columns -->
                <div class="stat-columns" v-if="hasPolygons">
                    <div class="stat-column" v-for="(column, colIdx) in detailColumns" :key="colIdx">
                        <div class="stat-card" v-for="section in column" :key="section.title">
                            <div class="stat-card-header">
                                <fa :icon="['fas', section.icon]" class="mr-2" />
                                <span>{{ section.title }}</span>
                            </div>
                            <div class="stat-card-body">
                                <div class="stat-line" v-for="row in section.rows" :key="row.key">
                                    <span class="stat-line-label">{{ row.label }}</span>
                                    <span class="stat-line-value">
                                        <template v-if="statsObj[row.key] !== undefined">{{ statsObj[row.key] }}</template>
                                        <span v-else-if="dataLoading" class="spinner-border spinner-border-sm cell-spinner" role="status"></span>
                                        <template v-else>{{ row.fallback }}</template>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #modal-footer>
            <b-row class="w-100 justify-content-end">
                <b-button class="close-btn px-4 py-2" @click="closeStatsModal()">Close</b-button>
            </b-row>
        </template>

        <!-- Confirmation Modal (TRT / LiDAR) -->
        <b-modal
          id="confirmLidarRequest"
          size="md"
          content-class="sub-menu-transparent"
          title="Data Request Confirmation"
        >
            <template #modal-header>
                <div class="d-flex align-items-center justify-content-between w-100">
                    <h5 class="mb-0 font-weight-semibold text-white">LiDAR Data Request Confirmation</h5>
                </div>
            </template>
            <template #modal-footer>
                <b-row class="w-100 justify-content-end">
                    <b-button class="close-btn px-4 py-2" @click="closeLidarConfirmation()">Close</b-button>
                </b-row>
            </template>
            <b-container>
                <b-row class="py-2 text-center">
                    <b-col>
                        LiDAR Data request with total segments length of <b class="highlight">{{ requestedSample['All segments length'] }}</b> will be sent to DMP.
                    </b-col>
                </b-row>
                <b-row class="py-2 text-center">
                    <b-col>Please confirm and click submit to send the request.</b-col>
                </b-row>
                <b-row class="py-2 w-100 justify-content-center" no-gutters>
                    <b-button class="confirm-btn px-3 py-2" @click="sendLidarRequest()">Submit</b-button>
                </b-row>
            </b-container>
        </b-modal>

    </b-modal>
</template>

<style>
/* Modal shell */
/* Width: id-scoped so it isn't overridden by the shared .modal-dialog-full
   rule (~1040px) defined in the sibling DDT / Information modals. */
#TRTStatsModal .modal-dialog-full {
    width: 37.5rem;
    max-width: 95vw;
    margin: 0.5rem auto;
}

@media (max-width: 620px) {
    #TRTStatsModal .modal-dialog-full {
        width: 96vw;
        max-width: 96vw;
    }
}
#TRTStatsModal .modal-content {
    border: none;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(6, 60, 109, 0.18);
}

#TRTStatsModal .modal-header {
    background: linear-gradient(135deg, #063c6d 0%, #0a4f8f 100%);
    color: white !important;
    padding: 0.85rem 1.35rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

#confirmLidarRequest .modal-header {
    background: linear-gradient(135deg, #063c6d 0%, #0a4f8f 100%);
    color: white !important;
    padding: 0.85rem 1.35rem;
}

/* Header title + download */
#TRTStatsModal .modal-title-wrap {
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

#TRTStatsModal .modal-title-icon {
    font-size: 1rem;
    opacity: 0.85;
}

#TRTStatsModal .download-button-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.125rem;
    height: 2.125rem;
    border-radius: 8px;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;
}

#TRTStatsModal .download-button-container:hover {
    background: rgba(255, 255, 255, 0.24);
    transform: translateY(-1px);
}

#TRTStatsModal .modal-body {
    padding-top: 0 !important;
}

/* Form area — sleek single horizontal row */
#TRTStatsModal .trt-form-area {
    background: #f7fafd;
    border-bottom: 1px solid #e1eaf3;
    padding: 1rem 1.375rem;
}

#TRTStatsModal .trt-form-row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;
    max-width: 57.5rem;
    margin: 0 auto;
}

#TRTStatsModal .trt-field {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
#TRTStatsModal .trt-field--type {
    width: 11.25rem;
    flex: 0 0 11.25rem;
}
#TRTStatsModal .trt-field--date {
    width: 12.5rem;
    flex: 0 0 12.5rem;
}
#TRTStatsModal .trt-field--notes {
    flex: 1 1 100%;
    width: 100%;
}

#TRTStatsModal .trt-form-label {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #4a6880;
    margin-bottom: 0.3rem;
}

/* Inputs */
#TRTStatsModal .trt-form-inner .form-control,
#TRTStatsModal .trt-form-inner .custom-select {
    border: 1px solid #dde5ef;
    border-radius: 8px;
    font-size: 0.85rem;
    color: #063c6d;
    background-color: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

#TRTStatsModal .trt-form-inner .form-control:focus,
#TRTStatsModal .trt-form-inner .custom-select:focus {
    border-color: #1a5da8;
    box-shadow: 0 0 0 3px rgba(26, 93, 168, 0.12);
    outline: none;
}

#TRTStatsModal .trt-form-inner textarea.form-control {
    resize: vertical;
    min-height: 2.75rem;
}

/* Section title above table */
#TRTStatsModal .trt-section-title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: #063c6d;
    border-left: 3px solid #1a5da8;
    padding-left: 0.55rem;
    line-height: 1;
}

#TRTStatsModal .stats-data {
    margin-top: 0.5rem;
    padding-bottom: 0.5rem;
}

/* Header row (title + request) */
#TRTStatsModal .stats-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.85rem;
}

/* Per-sample grouping */
#TRTStatsModal .sample-block {
    margin-bottom: 1.25rem;
}

#TRTStatsModal .sample-block:last-child {
    margin-bottom: 0;
}

#TRTStatsModal .sample-block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.35rem 0.15rem 0.6rem;
}

#TRTStatsModal .sample-block-title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: #1a5da8;
}

/* Cards */
#TRTStatsModal .stat-card {
    background: #ffffff;
    border: 1px solid #e2ebf5;
    border-radius: 10px;
    box-shadow: 0 3px 10px rgba(6, 60, 109, 0.05);
    overflow: hidden;
    margin-bottom: 1rem;
}

#TRTStatsModal .stat-card-header {
    display: flex;
    align-items: center;
    background: #f0f4f9;
    border-bottom: 1px solid #dee7f1;
    color: #063c6d;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 0.55rem 0.9rem;
}

#TRTStatsModal .stat-card-header .svg-inline--fa {
    color: #1a5da8;
}

#TRTStatsModal .stat-card-header-dark {
    background: linear-gradient(135deg, #063c6d 0%, #0a4f8f 100%);
    border-bottom: none;
    color: #ffffff;
}

#TRTStatsModal .stat-card-header-dark .svg-inline--fa {
    color: #ffffff;
    opacity: 0.9;
}

/* Detail rows inside a card */
#TRTStatsModal .stat-card-body {
    padding: 0.15rem 0;
}

#TRTStatsModal .stat-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.45rem 0.9rem;
    border-bottom: 1px solid #f0f4f8;
}

#TRTStatsModal .stat-line:last-child {
    border-bottom: none;
}

#TRTStatsModal .stat-line:hover {
    background: #f7fafd;
}

#TRTStatsModal .stat-line-label {
    font-size: 0.82rem;
    color: #3f5b76;
}

#TRTStatsModal .stat-line-value {
    font-size: 0.82rem;
    font-weight: 600;
    color: #0c78ff;
    white-space: nowrap;
}

/* Summary card: big side-by-side metrics */
#TRTStatsModal .stat-card-summary-body {
    display: flex;
    flex-wrap: wrap;
}

#TRTStatsModal .summary-metric {
    flex: 1 1 12.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.85rem 1.1rem;
    border-right: 1px solid #eef2f7;
}

#TRTStatsModal .summary-metric:last-child {
    border-right: none;
}

#TRTStatsModal .summary-metric-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #7089a3;
}

#TRTStatsModal .summary-metric-value {
    font-size: 1.4rem;
    font-weight: 700;
    color: #063c6d;
    line-height: 1.2;
}

/* Masonry columns for detail cards */
#TRTStatsModal .stat-columns {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    align-items: start;
}

#TRTStatsModal .stat-columns .stat-card {
    margin-bottom: 0;
}

#TRTStatsModal .stat-column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

@media (max-width: 991px) {
    #TRTStatsModal .stat-columns {
        grid-template-columns: 1fr;
    }
}

#TRTStatsModal .cell-spinner {
    width: 0.9rem;
    height: 0.9rem;
    border-width: 2px;
    color: #4A74A8;
    flex-shrink: 0;
}

#TRTStatsModal .highlightLoader {
    color: #4A74A8 !important;
}

#TRTStatsModal .stats-loading-banner {
    font-size: 0.72rem;
    font-weight: 600;
    color: #4a6880;
    gap: 0;
}

#TRTStatsModal .loading-banner-spinner {
    width: 0.75rem;
    height: 0.75rem;
    border-width: 2px;
    color: #4A74A8;
    flex-shrink: 0;
}

#TRTStatsModal .loading-banner-text {
    font-size: 0.72rem;
    color: #4a6880;
}

#TRTStatsModal .fade-enter-active,
#TRTStatsModal .fade-leave-active {
    transition: opacity 0.3s ease;
}
#TRTStatsModal .fade-enter,
#TRTStatsModal .fade-leave-to {
    opacity: 0;
}

#TRTStatsModal .request-btn {
    padding: 0.4rem 1.1rem;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    border-radius: 999px;
    border: none;
    color: #ffffff;
    background: #1a5da8;
    line-height: 1.4;
    box-shadow: 0 2px 6px rgba(6, 60, 109, 0.25);
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

#TRTStatsModal .request-btn:hover:not(:disabled) {
    background: #063c6d;
    box-shadow: 0 4px 10px rgba(6, 60, 109, 0.35);
    transform: translateY(-1px);
}

#TRTStatsModal .request-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
</style>

<script>
import axios from 'axios'
import * as XLSX from "xlsx-js-style";
import { getDateStringForFileName, styleWorksheet } from "../../../utils/utils"; 
import lidar_sample from '!!raw-loader!./../../../emails/lidar_sample.html'
import { mapGetters } from 'vuex';

// Full-width summary card
const SUMMARY_SECTION = {
    title: 'Summary',
    icon: 'chart-bar',
    rows: [
        { label: 'Total Segments Length',   key: 'All segments length',    fallback: '0.0 km' },
        ],
};

// Detail cards, laid out across three masonry columns (shown when polygons exist)
const DETAIL_COLUMNS = [
    [
        {
            title: 'OSM Segments',
            icon: 'map',
            rows: [
                { label: 'OSM Segments Length',      key: 'osm_total',          fallback: '0.0 km' },
                { label: 'OSM Motorway Length',      key: 'osm_motorway',       fallback: '0.0 km' },
                { label: 'OSM Motorway Link Length', key: 'osm_motorway_link',  fallback: '0.0 km' },
                { label: 'OSM Trunk Length',         key: 'osm_trunk',          fallback: '0.0 km' },
                { label: 'OSM Trunk Link Length',    key: 'osm_trunk_link',     fallback: '0.0 km' },
                { label: 'OSM Primary Length',       key: 'osm_primary',        fallback: '0.0 km' },
                { label: 'OSM Primary Link Length',  key: 'osm_primary_link',   fallback: '0.0 km' },
                { label: 'OSM Secondary Length',     key: 'osm_secondary',      fallback: '0.0 km' },
                { label: 'OSM Secondary Link Length', key: 'osm_secondary_link', fallback: '0.0 km' },
                { label: 'OSM Tertiary Length',      key: 'osm_tertiary',       fallback: '0.0 km' },
                { label: 'OSM Tertiary Link Length', key: 'osm_tertiary_link',  fallback: '0.0 km' },
                { label: 'OSM Residential Length',   key: 'osm_residential',    fallback: '0.0 km' },
                { label: 'OSM Service Length',       key: 'osm_service',        fallback: '0.0 km' },
                { label: 'OSM Unclassified Length',  key: 'osm_unclassified',   fallback: '0.0 km' },
            ],
        },
    ],
];

export default {
    name: 'DT_TRTStatsModal',
    props: ['polygonWkt', 'selectedExtent', 'user', 'polygons', 'lidarFlag', 'cart', 'lidar_wkt'],
    data() {
        return {
            statsObjs: [],
            requestName: null,
            dataLoading: true,
            osm_total: null,
            requestedSample: {},
            emailRecipients: [],
            deliveryType: null,
            deliveryDate: null,
            note: null
        }
    },
    computed: {
        ...mapGetters(['globalUserRole', 'globalAllowedUserModules', 'globalIsUserAllowed']),

        hasPolygons() {
            return this.polygons && this.polygons.length > 0;
        },

        // Card definitions used by the template
        summarySection() {
            return SUMMARY_SECTION;
        },
        detailColumns() {
            return DETAIL_COLUMNS;
        },

        // Flat list of every data row currently on screen — single source of truth reused by
        // downloadReports (keeps the xlsx in sync with the UI) and by isCellsLoading
        visibleStatRows() {
            const rows = [...SUMMARY_SECTION.rows];
            if (this.hasPolygons) {
                DETAIL_COLUMNS.forEach(column =>
                    column.forEach(section => rows.push(...section.rows))
                );
            }
            return rows;
        },

        isCellsLoading() {
            if (!this.statsObjs.length) return false;
            const dataKeys = this.visibleStatRows.map(r => r.key);
            return this.statsObjs.some(obj => dataKeys.some(key => obj[key] === undefined));
        },

        showRequestBtn() {
            return this.globalIsUserAllowed('Trail_Review_Tool_Sample_Request_Btn')
        },
    },
    methods: {
        closeStatsModal() {
            this.statsObjs = []
            this.requestedSample = {}
            this.$bvModal.hide('TRTStatsModal')
        },
        closeLidarConfirmation() {
            this.$bvModal.hide('confirmLidarRequest')
        },
        parseStatsResponse(content) {
            if (!content || content.errors) return {};
            const items = Array.isArray(content) ? content : [content];
            return Object.fromEntries(
                items.map(item => {
                    const raw = item.value ?? 0;
                    const value = (item.Key === 'intersection_count' || item.Key === 'splits_merges_count')
                        ? raw
                        : (raw / 1000).toFixed(2) + ' km';
                    return [item.Key, value];
                })
            );
        },
        async generateStatistics() {
            const geometries = (this.polygons && this.polygons.length)
                ? this.polygons
                : (this.lidar_wkt ? [this.lidar_wkt] : []);

            if (!geometries.length || !this.selectedExtent) return;
            if (!sessionStorage.token) { this.$router.push({ name: 'Login' }); return; }

            this.dataLoading = true;
            this.statsObjs = geometries.map(() => ({}));

            // Flatten every card row into a superset so each key gets a fallback,
            // regardless of which cards the template is currently showing.
            const allRows    = [...SUMMARY_SECTION.rows, ...DETAIL_COLUMNS.flat().flatMap(s => s.rows)];
            const fallbackMap = Object.fromEntries(allRows.map(r => [r.key, r.fallback]));
            const allKeys    = allRows.map(r => r.key);
            const osmKeys    = allKeys.filter(k => k.startsWith('osm_'));       // resolved by the OSM call
            const allSegKeys = allKeys.filter(k => !k.startsWith('osm_'));      // resolved by the all-segment call

            const applyFallbacks = (index, partial, keys) => {
                const patched = { ...partial };
                keys.forEach(key => {
                    if (patched[key] === undefined) patched[key] = fallbackMap[key];
                });
                this.$set(this.statsObjs, index, Object.assign({}, this.statsObjs[index], patched));
            };

            const lidar_wkt = this.lidar_wkt ? this.lidar_wkt[0] : null;

            const allDone = geometries.map((geom, index) => {
                const osmPromise = this.getOSMSegLength(this.selectedExtent, geom, lidar_wkt).then(partial => {
                    const osm_total = Object.entries(partial)
                        .filter(([key]) => key.startsWith('osm_') && key !== 'osm_construction')
                        .reduce((sum, [_, value]) => sum + (parseFloat(value?.toString().replace(' km', '')) || 0), 0)
                        .toFixed(2);
                    applyFallbacks(index, { ...partial, osm_total: `${osm_total} km` }, osmKeys);
                });

                return Promise.all([
                    this.getAllSegLength(this.selectedExtent, geom, lidar_wkt)
                        .then(p => applyFallbacks(index, p, allSegKeys)),
                    osmPromise,
                ]);
        });

            await Promise.all(allDone);
            this.dataLoading = false;
        },
        parseStatsResponse(content) {
            if (!content || content.errors) return {};
            const items = Array.isArray(content) ? content : [content];
            return Object.fromEntries(
                items.map(item => {
                    const raw = item.value ?? 0;
                    const value = (item.Key === 'intersection_count' || item.Key === 'splits_merges_count')
                        ? raw
                        : (raw / 1000).toFixed(2) + ' km';
                    return [item.Key, value];
                })
            );
        },
        async getAllSegLength(region, polygon, lidar_wkt) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/trailReviewTool/getPolygonSegmentLength`,
                    { region, polygon, lidar_wkt },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );

                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async getOSMSegLength(region, polygon, lidar_wkt) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/trailReviewTool/getPolygonOSMSegmentLength`,
                    { region, polygon, lidar_wkt },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        reset() {
            this.requestName = null
            this.deliveryDate = null
            this.note = null
            this.dataLoading = true
            this.statsObjs = []
        },
        openConfirmation(statsObj, index) {
            this.requestedSample = statsObj;
            this.requestedSample['wkt'] = this.polygons[index];
            this.$bvModal.show('confirmSampleRequest')
        },
        submitRequest() {
          this.sendLidarRequest()
        },
        sendLidarRequest() {
            if (sessionStorage.token) {
                axios.post(
                    `${process.env.API_LOCATION}/api/trailReviewTool/insert_requested_lidar_data`,
                    {
                        region:                   this.selectedExtent,
                        trails_length:            this.requestedSample['All segments length'],
                        osm_motorway_length:      this.requestedSample['osm_motorway'],
                        osm_motorway_link_length: this.requestedSample['osm_motorway_link'],
                        osm_trunk_length:         this.requestedSample['osm_trunk'],
                        osm_trunk_link_length:    this.requestedSample['osm_trunk_link'],
                        osm_primary_length:       this.requestedSample['osm_primary'],
                        osm_primary_link_length:  this.requestedSample['osm_primary_link'],
                        osm_secondary_length:     this.requestedSample['osm_secondary'],
                        osm_secondary_link_length:this.requestedSample['osm_secondary_link'],
                        osm_length:               this.requestedSample['osm_total'],
                        wkt:                      this.requestedSample['wkt'],
                        user:                     this.user.name,
                        deliveryType:             this.deliveryType,
                        deliveryDate:             this.deliveryDate,
                        note:                     this.note,
                        trails:                   this.cart.map(item => item.pc_id),
                        user_id:                  this.user.id
                    },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                )
                .then(response => {
                    const content = response.data
                    if (content.errors) {
                        console.log('getPolygonMapData API Call Error')
                    } else {
                        this.$bvModal.hide('TRTStatsModal')
                        this.$emit('admin-notification', {
                            region: this.selectedExtent, 
                            deliveryDate: this.deliveryDate,
                            deliveryType: this.deliveryType
                        })
                        this.$emit('removePolygons')
                        this.$emit('clearCart')
                        this.$bvToast.toast('Sending Email to Admin!', {
                            autoHideDelay: 1000,
                            variant: 'info',
                            solid: true,
                            title: 'In progress..',
                            toaster: 'b-toaster-bottom-right'
                        })
                        this.sendEmail()
                        let msg = `Your sample request has been successfully submitted and is currently under review.
                                    \n\nOur team will evaluate your request and update its status as it progresses.
                                    \n\nYou will receive a notification when there is an update.`
                                    
                        this.$emit('notification', {msg: msg, user_id: this.user.id})
                    }
                })
                .catch(function (error) {
                    console.log(error)
                    self.feedback = error.response.data.errors[0].msg
                    self.showFeedback()
                })
            } else {
                this.$router.push({ name: 'Login' })
            }
        },
        sendEmail() {
            const s   = this.requestedSample;
            const get = (key, fallback = '0.0 km') => s[key] || fallback;

            const All_segments_length                           = get('All segments length')
            const fa_active_construction_length                 = get('FA Active Construction')
            const fa_compromised_road_condition_length          = get('FA Compromised Road Condition')
            const fa_construction_completed_not_acquired_length = get('FA Construction Completed Not Acquired')
            const Dropped_segments_length                       = get('Dropped segments length')
            const osm_motorway_length                           = get('osm_motorway')
            const osm_motorway_link_length                      = get('osm_motorway_link')
            const osm_trunk_length                              = get('osm_trunk')
            const osm_trunk_link_length                         = get('osm_trunk_link')
            const OSM_Primary_Length                            = get('osm_primary')
            const osm_primary_link_length                       = get('osm_primary_link')
            const osm_secondary_length                          = get('osm_secondary')
            const osm_secondary_link_length                     = get('osm_secondary_link')
            const osm_tertiary_length                           = get('osm_tertiary')
            const osm_tertiary_link_length                      = get('osm_tertiary_link')
            const osm_residential_length                        = get('osm_residential')
            const osm_service_length                            = get('osm_service')
            const osm_unclassified_length                       = get('osm_unclassified')
            const access_ramp_length                            = get('RAMP')
            const local_length                                  = get('LOCAL')
            const interchange_length                            = get('INTERCHANGE')
            const non_controlled_length                         = get('NON_CONTROLLED')
            const controlled_length                             = get('CONTROLLED')
            const roundabout_length                             = get('ROUNDABOUT')
            const turn_maneuver_length                          = get('TURN_MANEUVER')
            const merges_splits_count                           = get('splits_merges_count', '0')
            const intersection_count                            = get('intersection_count',  '0')
            const osm_length                                    = get('osm_total')
            const delivery_type = this.deliveryType
            const delivery_date = this.deliveryDate
            const note          = this.note
            const trails = this.cart?.map(item => item.pc_id).join('\n') || ''

            const isValidEmail = [
                All_segments_length, fa_active_construction_length,
                fa_compromised_road_condition_length, fa_construction_completed_not_acquired_length,
                Dropped_segments_length, osm_motorway_length, osm_motorway_link_length,
                osm_trunk_length, osm_trunk_link_length, OSM_Primary_Length, osm_primary_link_length,
                osm_secondary_length, osm_secondary_link_length, osm_tertiary_length,
                osm_tertiary_link_length, osm_residential_length, osm_service_length,
                osm_unclassified_length, access_ramp_length, local_length, interchange_length,
                non_controlled_length, controlled_length, roundabout_length, turn_maneuver_length,
            ].some(v => v !== '0.0 km') || merges_splits_count !== '0' || intersection_count !== '0';

            const fromEmail    = 'mmp-info@dmp-maps.com'
            const emailSubject = 'New data sample request received from MMP'
            const year         = new Date().getFullYear()

            const emailBody = lidar_sample
                .replace('{{ name }}',                                          this.user.name)
                .replace('{{ email }}',                                         this.user.email)
                .replace('{{ polygonWkt }}',                                    s['wkt'])
                .replace('{{ selectedExtent }}',                                this.selectedExtent)
                .replace('{{ all_segments_length }}',                           All_segments_length)
                .replace('{{ osm_length }}',                                    osm_length)
                .replace('{{ fa_active_construction_length }}',                 fa_active_construction_length)
                .replace('{{ fa_compromised_road_condition_length }}',          fa_compromised_road_condition_length)
                .replace('{{ fa_construction_completed_not_acquired_length }}', fa_construction_completed_not_acquired_length)
                .replace('{{ dropped_segments_length }}',                       Dropped_segments_length)
                .replace('{{ osm_motorway_length }}',                           osm_motorway_length)
                .replace('{{ osm_motorway_link_length }}',                      osm_motorway_link_length)
                .replace('{{ osm_trunk_length }}',                              osm_trunk_length)
                .replace('{{ osm_trunk_link_length }}',                         osm_trunk_link_length)
                .replace('{{ osm_primary_length }}',                            OSM_Primary_Length)
                .replace('{{ osm_primary_link_length }}',                       osm_primary_link_length)
                .replace('{{ osm_secondary_length }}',                          osm_secondary_length)
                .replace('{{ osm_secondary_link_length }}',                     osm_secondary_link_length)
                .replace('{{ osm_tertiary_length }}',                           osm_tertiary_length)
                .replace('{{ osm_tertiary_link_length }}',                      osm_tertiary_link_length)
                .replace('{{ osm_residential_length }}',                        osm_residential_length)
                .replace('{{ osm_service_length }}',                            osm_service_length)
                .replace('{{ osm_unclassified_length }}',                       osm_unclassified_length)
                .replace('{{ access_ramp_length }}',                            access_ramp_length)
                .replace('{{ local_length }}',                                  local_length)
                .replace('{{ interchange_length }}',                            interchange_length)
                .replace('{{ non_controlled_length }}',                         non_controlled_length)
                .replace('{{ controlled_length }}',                             controlled_length)
                .replace('{{ roundabout_length }}',                             roundabout_length)
                .replace('{{ turn_maneuver_length }}',                          turn_maneuver_length)
                .replace('{{ merges_splits_count }}',                           merges_splits_count)
                .replace('{{ intersection_count }}',                            intersection_count)
                .replace('{{ year }}',                                          year)
                .replace('{{ delivery_type }}',                                 delivery_type)
                .replace('{{ delivery_date }}',                                 delivery_date)
                .replace('{{ note }}',                                          note)
                .replace('{{ trails }}',                                        trails)

            const now      = new Date();
            const mm       = String(now.getMonth() + 1).padStart(2, '0');
            const dd       = String(now.getDate()).padStart(2, '0');
            const yyyy     = now.getFullYear();
            const hh       = String(now.getHours()).padStart(2, '0');
            const min      = String(now.getMinutes()).padStart(2, '0');
            const fileName = `lidar_sample_${mm}_${dd}_${yyyy}_${hh}_${min}`   
            const endpoint = 'sendEmailWithShapefile';

            if (sessionStorage.token) {
                if (isValidEmail) {
                    axios.post(
                        `${process.env.API_LOCATION}/api/email/${endpoint}`,
                        {
                            fromEmail:    fromEmail,
                            toEmail:      this.emailRecipients,
                            emailSubject: emailSubject,
                            emailBody:    emailBody,
                            wkt:          s['wkt'],
                            fileName:     fileName
                        },
                        { headers: { 'x-auth-token': sessionStorage.token } }
                    )
                    .then(response => {
                        const content = response.data
                        if (content.errors) {
                            console.log('sendEmail API Call Error')
                        } else {
                            if (content.some(obj => obj.status !== 'bounced' || obj.status !== 'rejected')) {
                                this.$bvToast.toast('Data Sample request Email sent.', {
                                    autoHideDelay: 5000,
                                    variant: 'success',
                                    solid: true,
                                    title: 'Email Sent',
                                    toaster: 'b-toaster-bottom-right'
                                })
                                this.$bvModal.hide('TRTStatsModal')
                            }
                        }
                    })
                    .catch(function (error) {
                        console.log(error)
                        self.feedback = error.response.data.errors[0].msg
                        self.showFeedback()
                    })
                } else {
                    this.$bvToast.toast('There is no data to generate a sample.', {
                        autoHideDelay: 7000,
                        variant: 'danger',
                        solid: true,
                        title: 'Email Not Sent',
                        toaster: 'b-toaster-bottom-right'
                    })
                    this.$bvModal.hide('TRTStatsModal')
                }
            } else {
                this.$router.push({ name: 'Login' })
            }
        },
        getEmailRecipients() {
            let module_name = ''
            module_name = 'lidar_sample'
            if (sessionStorage.token) {
                axios.get(`${process.env.API_LOCATION}/api/email/getRecipients`, {
                    headers: { 'x-auth-token': sessionStorage.token },
                    params:  { module: module_name }
                })
                .then(response => {
                    this.emailRecipients = response.data[0].recipients;
                })
                .catch(function (error) {
                    console.log(error)
                })
            } else {
                console.log('No User Token')
            }
        },
        downloadReports() {
            let wb = XLSX.utils.book_new()
            const deepCopyStats = JSON.parse(JSON.stringify(this.statsObjs))
            let ordered = new Map(Object.entries(deepCopyStats))

            ordered.forEach((statsObj, index) => {
                let ws_data = new Map()

                ws_data.set('Region',       this.selectedExtent)
                ws_data.set('deliveryType', this.deliveryType || ' ')
                ws_data.set('deliveryDate', this.deliveryDate || ' ')
                ws_data.set('note',         this.note         || ' ')
                ws_data.set('', '')
                ws_data.set('Attribute', 'Value')

                // visibleStatRows keeps the xlsx in sync with exactly what the UI shows
                this.visibleStatRows.filter(r => !r.isSection).forEach(row => {
                    ws_data.set(row.label, statsObj[row.key] || row.fallback)
                })

                const jsonStr = JSON.parse(JSON.stringify([...ws_data]))
                let ws = XLSX.utils.aoa_to_sheet(jsonStr)
                styleWorksheet(ws, { leftAlignNumbers: true, boldCols: [0], boldRows: [6] });
                XLSX.utils.book_append_sheet(wb, ws, 'Request_' + (parseInt(index) + 1))
            })

            const timestampstring = getDateStringForFileName()
            XLSX.writeFile(wb, `Statistics_Report_${timestampstring}.xlsx`)
        },
    },
    mounted() {
        this.getEmailRecipients()
    },
    watch: {
        selectedExtent() {
            this.getEmailRecipients();
        }
    }
}
</script>